import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useNavigate } from 'react-router-dom'

import { FilterMatchMode, FilterOperator, FilterService } from 'primereact/api'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { DataTable, DataTableFilterMeta } from 'primereact/datatable'
import { useMountEffect } from 'primereact/hooks'
import { Messages } from 'primereact/messages'

import {
  AttachmentDownloadButton,
  AttachmentOpenInTabButton,
} from '../../../../../components/attachments'
import {
  DateBodyTemplate,
  DateFilterTemplate,
  PriceBodyTemplate,
  PriceFilterTemplate,
  TestAndSourceBodyTemplate,
  TestAndSourceFilterTemplate,
} from '../../../../../components/tableTemplates'
import { EInvoiceAttachment } from '../../../../../data/models'
import { useColumnToggleDirect } from '../../../../../hooks/useColumnToggle'
import { EmptyMessage } from '../../../../../utils/wrapper/ConfigurableTable/EmptyMessage'
import { TestAndSourceFilter, testAndSourceFilterService } from '../../../utils'
import { EInvoiceHistory } from '../../data'
import { useEInvoiceHistory } from '../../data/hook'
import { convertEInvoiceHistoryDates } from '../../utils'
import {
  invoiceTypeBodyTemplate,
  invoiceTypeFilterTemplate,
  statusBodyTemplate,
  statusFilterTemplate,
} from './templates'

const STORAGE_KEY = 'e-invoice-history-visible-columns'

type Props = {
  search: string
  setSearch: (value: string) => void
}

export const Table: React.FC<Props> = ({ search, setSearch }) => {
  const { t } = useTranslation()
  const { data } = useEInvoiceHistory()
  const navigate = useNavigate()
  const msgs = useRef<Messages>(null)

  const toggleableColumns = [
    {
      field: 'meta',
      label: t('eInvoiceOverview.table.columns.testAndSource.header'),
    },
    {
      field: 'invoiceType',
      label: t('eInvoiceHistory.table.columns.invoiceType.header'),
    },
    {
      field: 'invoiceDate',
      label: t('eInvoiceHistory.table.columns.invoiceDate.header'),
    },
    {
      field: 'grossPrice',
      label: t('eInvoiceHistory.table.columns.grossPrice.header'),
    },
    {
      field: 'documentName',
      label: t('eInvoiceHistory.table.columns.documentName.header'),
    },
    {
      field: 'submittedBy',
      label: t('eInvoiceHistory.table.columns.submittedBy.header'),
    },
    {
      field: 'submittedAt',
      label: t('eInvoiceHistory.table.columns.submittedAt.header'),
    },
  ]

  const { show, navigationColumnHeader } = useColumnToggleDirect(
    toggleableColumns,
    STORAGE_KEY,
    true,
  )

  // Filters
  const [filters, setFilters] = useState<DataTableFilterMeta>(defaultFilters)

  useEffect(() => {
    initFilters()
  }, [])

  const initFilters = () => {
    setFilters(defaultFilters)
    setSearch('')
  }

  useEffect(() => {
    let _filters = { ...filters }

    // @ts-ignore
    _filters['global'].value = search

    setFilters(_filters)
  }, [search])

  const clearFilter = () => {
    initFilters()
  }

  // Message
  useMountEffect(() => {
    if (msgs.current) {
      msgs.current.clear()
      msgs.current.show({
        id: '1',
        sticky: true,
        severity: 'info',
        summary: t('eInvoiceHistory.messages.summary'),
        detail: t('eInvoiceHistory.messages.detail'),
        closable: false,
      })
    }
  })

  const getOriginalAttachment = (
    attachments: EInvoiceAttachment[] | undefined,
    originalDocumentName: string | undefined,
  ): EInvoiceAttachment | undefined => {
    if (!attachments?.length || !originalDocumentName) return undefined

    return attachments.find(
      attachment => attachment.FileName === originalDocumentName,
    )
  }

  const actionBodyTemplate = (rowData: EInvoiceHistory) => {
    const originalAttachment = getOriginalAttachment(
      rowData.attachments,
      rowData.documentName,
    )

    return (
      <div className="flex flex-row gap-1 justify-center">
        {/* Only show additional attachments if there are more than the original e-invoice */}
        {rowData.attachments.length > 1 && (
          <Button
            icon="pi pi-paperclip"
            onClick={() =>
              navigate(`documents/${rowData.id}`, {
                state: { attachments: rowData.attachments },
              })
            }
            aria-label={t(
              'eInvoiceHistory.table.columns.attachment.tooltips.showDocuments',
            )}
            text
            tooltip={t(
              'eInvoiceHistory.table.columns.attachment.tooltips.showDocuments',
            )}
            tooltipOptions={{ position: 'top' }}
          />
        )}

        {/* Only show original e-invoice actions if it exists */}
        {originalAttachment?.DocServUri &&
          originalAttachment?.MimeType &&
          originalAttachment?.FileName && (
            <>
              <AttachmentOpenInTabButton
                key={`att-tab-${rowData.id}`}
                fullUrl={originalAttachment?.DocServUri}
                mimeType={originalAttachment?.MimeType}
                fileName={originalAttachment?.FileName}
                aria-label={t(
                  'eInvoiceHistory.table.columns.attachment.tooltips.open',
                )}
                tooltip={t(
                  'eInvoiceHistory.table.columns.attachment.tooltips.open',
                )}
                tooltipOptions={{ position: 'top' }}
              />

              <AttachmentDownloadButton
                key={`att-download-${rowData.id}`}
                fullUrl={originalAttachment?.DocServUri}
                mimeType={originalAttachment?.MimeType}
                fileName={originalAttachment?.FileName}
                aria-label={t(
                  'eInvoiceHistory.table.columns.attachment.tooltips.download',
                )}
                tooltip={t(
                  'eInvoiceHistory.table.columns.attachment.tooltips.download',
                )}
                tooltipOptions={{ position: 'top' }}
              />
            </>
          )}
      </div>
    )
  }

  const emptyMessage = (
    <EmptyMessage
      title={t('eInvoiceHistory.table.emptyMessageTitle')}
      details={t('eInvoiceHistory.table.emptyMessageDetails')}
      value={data}
      clearFilter={clearFilter}
    />
  )

  return (
    <div className="card">
      <Outlet />

      <Messages ref={msgs} />

      <DataTable
        value={convertEInvoiceHistoryDates(data)}
        dataKey="id"
        filters={filters}
        currentPageReportTemplate={t('common.table.numberOfEntries')}
        paginator
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        sortField="invoiceDate"
        sortOrder={1}
        rows={20}
        rowsPerPageOptions={[20, 50, 100]}
        style={{ fontSize: '90%' }}
        scrollable
        emptyMessage={emptyMessage}
      >
        {show('meta') && (
          <Column
            key="meta"
            field="meta"
            header={t('eInvoiceOverview.table.columns.testAndSource.header')}
            body={(rowData: EInvoiceHistory) =>
              TestAndSourceBodyTemplate(
                rowData?.meta?.controlInformation?.trialFlag,
                rowData?.meta?.controlInformation?.processingType,
              )
            }
            filter
            filterElement={TestAndSourceFilterTemplate}
            filterMatchMode={FilterMatchMode.EQUALS}
            showFilterMatchModes={false}
            align="center"
          />
        )}
        <Column
          key="invoiceNumber"
          field="invoiceNumber"
          header={t('eInvoiceHistory.table.columns.invoiceNumber.header')}
          filter
          filterMatchMode={FilterMatchMode.STARTS_WITH}
          filterPlaceholder={t(
            'eInvoiceHistory.table.columns.invoiceNumber.filterPlaceholder',
          )}
          sortable
        />

        {show('invoiceType') && (
          <Column
            key="invoiceType"
            field="invoiceType"
            header={t('eInvoiceHistory.table.columns.invoiceType.header')}
            body={(rowData: EInvoiceHistory) =>
              invoiceTypeBodyTemplate(rowData.invoiceType)
            }
            filter
            filterMatchMode={FilterMatchMode.EQUALS}
            filterElement={invoiceTypeFilterTemplate}
            filterPlaceholder={t(
              'eInvoiceHistory.table.columns.invoiceType.filterPlaceholder',
            )}
            sortable
          />
        )}

        {show('invoiceDate') && (
          <Column
            key="invoiceDate"
            field="invoiceDate"
            header={t('eInvoiceHistory.table.columns.invoiceDate.header')}
            body={(rowData: EInvoiceHistory) =>
              DateBodyTemplate(rowData.invoiceDate)
            }
            filter
            filterElement={DateFilterTemplate}
            filterMatchMode={FilterMatchMode.DATE_IS}
            sortable
          />
        )}

        {show('grossPrice') && (
          <Column
            key="grossPrice"
            field="grossPrice"
            header={t('eInvoiceHistory.table.columns.grossPrice.header')}
            body={(rowData: EInvoiceHistory) =>
              PriceBodyTemplate(rowData.grossPrice, { currency: 'EUR' })
            }
            filter
            filterMatchMode={FilterMatchMode.EQUALS}
            filterElement={options =>
              PriceFilterTemplate(
                options,
                ' €',
                t('eInvoiceHistory.table.columns.grossPrice.filterPlaceholder'),
              )
            }
            filterPlaceholder={t(
              'eInvoiceHistory.table.columns.grossPrice.filterPlaceholder',
            )}
            align="right"
            sortable
          />
        )}

        {show('documentName') && (
          <Column
            key="documentName"
            field="documentName"
            header={t('eInvoiceHistory.table.columns.documentName.header')}
            filter
            filterMatchMode={FilterMatchMode.STARTS_WITH}
            filterPlaceholder={t(
              'eInvoiceHistory.table.columns.documentName.filterPlaceholder',
            )}
            sortable
          />
        )}

        {show('submittedBy') && (
          <Column
            key="submittedBy"
            field="submittedBy"
            header={t('eInvoiceHistory.table.columns.submittedBy.header')}
            filter
            filterMatchMode={FilterMatchMode.STARTS_WITH}
            filterPlaceholder={t(
              'eInvoiceHistory.table.columns.submittedBy.filterPlaceholder',
            )}
            sortable
          />
        )}

        {show('submittedAt') && (
          <Column
            key="submittedAt"
            field="submittedAt"
            header={t('eInvoiceHistory.table.columns.submittedAt.header')}
            body={(rowData: EInvoiceHistory) =>
              DateBodyTemplate(rowData.submittedAt, undefined, true)
            }
            filter
            filterMatchMode={FilterMatchMode.DATE_IS}
            filterElement={DateFilterTemplate}
            sortable
          />
        )}

        <Column
          key="status"
          field="status"
          header={t('eInvoiceHistory.table.columns.status.header')}
          body={(rowData: EInvoiceHistory) =>
            statusBodyTemplate(rowData.status)
          }
          filter
          filterMatchMode={FilterMatchMode.EQUALS}
          filterElement={any => statusFilterTemplate(any)}
          filterPlaceholder={t(
            'eInvoiceHistory.table.columns.status.filterPlaceholder',
          )}
          frozen
          alignFrozen="right"
        />

        <Column
          field="attachment"
          body={actionBodyTemplate}
          header={
            <div className="flex justify-content-between align-items-center gap-5 w-full">
              <span>
                {t('eInvoiceHistory.table.columns.attachment.header')}
              </span>
              {navigationColumnHeader}
            </div>
          }
          frozen
          alignFrozen="right"
        />
      </DataTable>
    </div>
  )
}

const defaultFilters: DataTableFilterMeta = {
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  meta: {
    value: null,
    matchMode: FilterMatchMode.CUSTOM,
  },
  invoiceNumber: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  invoiceType: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
  invoiceDate: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
  },
  grossPrice: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
  documentName: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  submittedBy: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  submittedAt: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
  },
  status: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
}

FilterService.register(
  'custom_meta',
  (value: EInvoiceHistory['meta'], filters: TestAndSourceFilter) => {
    return testAndSourceFilterService(
      value.controlInformation.trialFlag,
      value.controlInformation.processingType,
      filters,
    )
  },
)
